/***
 * Created on 2025/7/2
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect} from 'react';
import {Navigate, Outlet, useLocation, useParams} from "react-router-dom";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useQuery} from "@tanstack/react-query";
import {useOrganization} from "../../context/OrganizationContext";
import {UserRolePermissions} from "../../service/auth";
import OrgContentPlaceholderComponent from "../organization-content/OrgContentPlaceholderComponent";

// Seed the permissions query from currentOrg.current_user (which the
// dashboard payload populates). Returning a UserRolePermissions instance
// here lets React Query skip the loading skeleton on first paint while it
// still fetches /user/<id>/permissions in the background to confirm.
// See follow-up: SSE/WebSocket push for real-time permission changes
// would let us drop the background fetch entirely.
function _initialPermissionsFromContext(oid) {
    try {
        const co = JSON.parse(window.localStorage.getItem('currentOrg') || 'null');
        if (co && co.organization_id === oid && co.current_user) {
            return new UserRolePermissions({
                role: co.current_user.role,
                permissions: co.current_user.permissions || {},
            });
        }
    } catch (e) {
        // Bad JSON in storage — fall through to the fetch path.
    }
    return undefined;
}

// Route guard per the Org Role matrix (Confluence 1071979175). Returns a
// redirect path when `page` is not viewable for the user's role, else null.
//   - Owner: unrestricted.
//   - Cloud Space (Manage/View): owner-only — blocks User Manager + Member.
//   - Device page: open to non-owners; the backend serves a role-scoped device
//     list, so an unassigned non-owner just sees the empty device content (no
//     redirect needed). Non-owners only see/operate on their assigned devices.
//   - Member: limited to Org Profile + Sites (TEM61510-22480: members read the
//     sites page via the role-redacted projection) + the Device page. User
//     Manager keeps sites, groups, activity log, accounts, security.
// Exported for unit testing.
export function orgPageRedirect(permissions, page, oid) {
    if (!permissions || permissions.role === 'owner') return null;
    // Cloud Space is owner-only.
    if (page === 'cloud-space') return `/${oid}/settings`;
    // Members are limited to Org Profile, Sites, and the (role-scoped) Device page.
    const memberAllowed = ['settings', 'sites', 'devices'];
    if (permissions.role === 'member' && !memberAllowed.includes(page)) {
        return `/${oid}/settings`;
    }
    return null;
}

function OrganizationPagesLayout({}) {
    const {oid} = useParams();
    const { OrganizationAPI } = useOrganizationApi();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));

    const {data, isFetched, refetch} = useQuery({
        queryKey: ['get_user_organization_role_permissions', oid],
        queryFn: async () => {
            const response = await OrganizationAPI.get(`organization/${oid}/user/${currentUser?.user_id}/permissions`);
            const permissions = new UserRolePermissions(response?.result);
            return permissions;
        },
        enabled: !!oid && !!currentUser?.user_id,
        // Optimistic placeholder from dashboard payload — surfaced as `data`
        // immediately so we can render without a skeleton on the common
        // dashboard-then-click flow. Unlike `initialData`, `placeholderData`
        // does NOT mark the query as fetched, so queryFn still runs and
        // refreshes the data once the server confirms.
        placeholderData: () => _initialPermissionsFromContext(oid),
        // Always re-fetch on remount; the placeholder data covers the visual
        // gap while the network request is in flight.
        staleTime: 0,
        // Catch cross-tab/cross-session edits when the user returns focus.
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        sessionStorage.setItem('current_organization', oid);

        return () => {
            sessionStorage.removeItem('current_organization');
        }
    }, [oid]);

    const {pathname} = useLocation();

    // `data` may be the placeholder (seeded from dashboard's current_user) or
    // the resolved query result. Either way it's a UserRolePermissions
    // instance, so we can route off it immediately. Only fall back to the
    // skeleton when there's no placeholder AND the fetch hasn't resolved —
    // i.e. deep-link entry without dashboard context.
    if (!data && !isFetched) {
        return <OrgContentPlaceholderComponent/>
    }

    const redirect = orgPageRedirect(data, pathname.split('/')[2], oid);
    if (redirect) {
        return <Navigate to={redirect} replace />;
    }

    return (
        <Outlet context={{userPermissions:data, fetchPermissions:refetch}}/>
    );
}

export default OrganizationPagesLayout;